
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { LearningObjective, Teacher, UserRole } from '../types';
import { Target, Plus, Trash2, Edit2, X, Search, Upload, Printer, FileSpreadsheet, ArrowUpDown, Copy, BookOpen, Layers, Calendar, Award, Sparkles, RefreshCw, Check, FileText, Info, Filter, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../src/lib/firebase';
import ConfirmModal from '../components/ConfirmModal';

interface Props {
  objectives: LearningObjective[];
  onUpdate: (objectives: LearningObjective[]) => void;
  teacherSubject: string;
  activeUser: Teacher;
  schoolSettings: any;
  userRole?: UserRole;
}

const LearningObjectivesView: React.FC<Props> = ({ objectives, onUpdate, teacherSubject, activeUser, schoolSettings, userRole }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedDescriptionObj, setSelectedDescriptionObj] = useState<LearningObjective | null>(null);
  const [copiedDesc, setCopiedDesc] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [syncToast, setSyncToast] = useState<string | null>(null);
  
  // Available Subjects including from objectives and school settings
  const availableSubjectsFromObjectives = useMemo(() => {
    const fromObjs = objectives.map(obj => (obj.subject || '').trim()).filter(Boolean);
    const fromSettings = ((schoolSettings?.subjects || []) as string[]).map(s => (s || '').trim()).filter(Boolean);
    const set = new Set([...fromObjs, ...fromSettings]);
    return Array.from(set).sort();
  }, [objectives, schoolSettings?.subjects]);

  const [selectedSubjectFilter, setSelectedSubjectFilter] = useState<string>('Semua');
  const [selectedYearFilter, setSelectedYearFilter] = useState<string>('Semua');
  const [selectedSemesterFilter, setSelectedSemesterFilter] = useState<string>('Semua');

  const availableAcademicYears = useMemo(() => {
    const years = objectives.map(obj => (obj.academicYear || '').trim()).filter(Boolean);
    if (schoolSettings?.academicYear) {
      years.push(schoolSettings.academicYear.trim());
    }
    return Array.from(new Set(years)).sort();
  }, [objectives, schoolSettings?.academicYear]);

  const availableSemesters = useMemo(() => {
    const sems = objectives.map(obj => (obj.semester || '').trim()).filter(Boolean);
    const set = new Set([...sems, 'Ganjil', 'Genap']);
    return Array.from(set).filter(Boolean).sort();
  }, [objectives]);

  // Sync / Refresh directly from Firestore collection
  const handleSyncFromDatabase = async () => {
    setIsSyncing(true);
    try {
      const snap = await getDocs(collection(db, "learningObjectives"));
      const fetched: LearningObjective[] = snap.docs.map(d => ({
        id: d.id,
        ...(d.data() as any)
      }));
      if (fetched) {
        onUpdate(fetched);
        try {
          localStorage.setItem("smpn1_objectives", JSON.stringify(fetched));
        } catch (e) {}
        setSyncToast(`Berhasil memuat ${fetched.length} TP dari Database`);
        setTimeout(() => setSyncToast(null), 3500);
      }
    } catch (err) {
      console.error("Error syncing learningObjectives from Firestore:", err);
      setSyncToast("Gagal memuat TP dari database");
      setTimeout(() => setSyncToast(null), 3500);
    } finally {
      setIsSyncing(false);
    }
  };

  // Auto sync if objectives is empty on initial mount
  useEffect(() => {
    if (!objectives || objectives.length === 0) {
      handleSyncFromDatabase();
    }
  }, []);

  const [formData, setFormData] = useState({ 
    code: '', 
    element: '',
    scope: '',
    semester: '',
    academicYear: '',
    description: '',
    isSummative: false,
    isPracticum: false,
    subject: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'code' | 'description' | 'semester'>('code');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const resetFilters = () => {
    setSelectedSubjectFilter('Semua');
    setSelectedYearFilter('Semua');
    setSelectedSemesterFilter('Semua');
    setSearchTerm('');
  };

  const isAnyFilterActive = selectedSubjectFilter !== 'Semua' || selectedYearFilter !== 'Semua' || selectedSemesterFilter !== 'Semua' || searchTerm.trim() !== '';

  const filteredObjectives = useMemo(() => {
    const filtered = objectives.filter(obj => {
      if (userRole === UserRole.Admin) {
        if (selectedSubjectFilter !== 'Semua') {
          const isSubjectMatch = (obj.subject || '').trim().toLowerCase() === (selectedSubjectFilter || '').trim().toLowerCase();
          if (!isSubjectMatch) return false;
        }
      } else {
        const isSubjectMatch = (obj.subject || '').trim().toLowerCase() === (teacherSubject || '').trim().toLowerCase();
        if (!isSubjectMatch) return false;
      }

      if (selectedYearFilter !== 'Semua') {
        const isYearMatch = (obj.academicYear || '').trim().toLowerCase() === (selectedYearFilter || '').trim().toLowerCase();
        if (!isYearMatch) return false;
      }

      if (selectedSemesterFilter !== 'Semua') {
        const isSemesterMatch = (obj.semester || '').trim().toLowerCase() === (selectedSemesterFilter || '').trim().toLowerCase();
        if (!isSemesterMatch) return false;
      }

      const code = (obj.code || '').toLowerCase();
      const description = (obj.description || '').toLowerCase();
      const element = (obj.element || '').toLowerCase();
      const scope = (obj.scope || '').toLowerCase();
      const objId = (obj.id || '').toLowerCase();
      const subject = (obj.subject || '').toLowerCase();
      const term = (searchTerm || '').toLowerCase();

      return code.includes(term) || description.includes(term) || element.includes(term) || scope.includes(term) || objId.includes(term) || subject.includes(term);
    });

    return filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'code') {
        comparison = (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' });
      } else if (sortBy === 'description') {
        comparison = (a.description || '').localeCompare(b.description || '');
      } else if (sortBy === 'semester') {
        comparison = (a.semester || '').localeCompare(b.semester || '');
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [objectives, teacherSubject, selectedSubjectFilter, selectedYearFilter, selectedSemesterFilter, userRole, searchTerm, sortBy, sortOrder]);

  const getColorClasses = (index: number) => {
    const colors = [
      { 
        gradient: 'from-indigo-600 via-purple-600 to-indigo-700', 
        badgeBg: 'bg-white/20 border-white/30 text-white',
        cardBorder: 'hover:border-indigo-500/60 hover:shadow-indigo-500/20',
        accentText: 'text-indigo-600 dark:text-indigo-400',
        lightBg: 'bg-indigo-50/70 dark:bg-indigo-950/40 border-indigo-100 dark:border-indigo-900/50',
      },
      { 
        gradient: 'from-emerald-600 via-teal-600 to-cyan-700', 
        badgeBg: 'bg-white/20 border-white/30 text-white',
        cardBorder: 'hover:border-emerald-500/60 hover:shadow-emerald-500/20',
        accentText: 'text-emerald-600 dark:text-emerald-400',
        lightBg: 'bg-emerald-50/70 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900/50',
      },
      { 
        gradient: 'from-blue-600 via-sky-600 to-indigo-700', 
        badgeBg: 'bg-white/20 border-white/30 text-white',
        cardBorder: 'hover:border-sky-500/60 hover:shadow-sky-500/20',
        accentText: 'text-sky-600 dark:text-sky-400',
        lightBg: 'bg-sky-50/70 dark:bg-sky-950/40 border-sky-100 dark:border-sky-900/50',
      },
      { 
        gradient: 'from-amber-600 via-orange-600 to-red-600', 
        badgeBg: 'bg-white/20 border-white/30 text-white',
        cardBorder: 'hover:border-amber-500/60 hover:shadow-amber-500/20',
        accentText: 'text-amber-600 dark:text-amber-400',
        lightBg: 'bg-amber-50/70 dark:bg-amber-950/40 border-amber-100 dark:border-amber-900/50',
      },
      { 
        gradient: 'from-rose-600 via-pink-600 to-purple-700', 
        badgeBg: 'bg-white/20 border-white/30 text-white',
        cardBorder: 'hover:border-rose-500/60 hover:shadow-rose-500/20',
        accentText: 'text-rose-600 dark:text-rose-400',
        lightBg: 'bg-rose-50/70 dark:bg-rose-950/40 border-rose-100 dark:border-rose-900/50',
      },
    ];
    return colors[index % colors.length];
  };

  const handleOpenModal = (obj?: LearningObjective) => {
    if (obj) {
      setEditingId(obj.id);
      setFormData({ 
        code: obj.code,
        element: obj.element || '',
        scope: obj.scope || '',
        semester: obj.semester || '',
        academicYear: obj.academicYear || '',
        description: obj.description,
        isSummative: obj.isSummative || false,
        isPracticum: obj.isPracticum || false,
        subject: obj.subject || ''
      });
    } else {
      setEditingId(null);
      setFormData({ 
        code: `TP ${objectives.length + 1}`, 
        element: '',
        scope: '',
        semester: '',
        academicYear: '',
        description: '',
        isSummative: false,
        isPracticum: false,
        subject: selectedSubjectFilter !== 'Semua' ? selectedSubjectFilter : (availableSubjectsFromObjectives[0] || '')
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!formData.code || !formData.description) return;
    if (userRole === UserRole.Admin && !formData.subject) {
      alert('Mata Pelajaran harus diisi!');
      return;
    }

    const finalData = {
      code: formData.code,
      element: formData.element,
      scope: formData.scope,
      semester: formData.semester,
      academicYear: formData.academicYear,
      description: formData.description,
      isSummative: formData.isSummative,
      isPracticum: formData.isPracticum,
      subject: userRole === UserRole.Admin ? formData.subject : teacherSubject
    };

    if (editingId) {
      onUpdate(objectives.map(o => o.id === editingId ? { ...o, ...finalData } : o));
    } else {
      const newObj: LearningObjective = {
        id: `tp-${Date.now()}`,
        ...finalData
      };
      onUpdate([...objectives, newObj]);
    }
    setIsModalOpen(false);
  };

  const handleDelete = (id: string) => {
    const objective = objectives.find(o => o.id === id);
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Tujuan Pembelajaran',
      message: `Apakah Anda yakin ingin menghapus "${objective?.code || 'TP ini'}"? Data ini mungkin digunakan dalam agenda dan penilaian.`,
      onConfirm: () => {
        onUpdate(objectives.filter(o => o.id !== id));
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const getDuplicateTpCode = (originalCode: string, existingCodes: string[]): string => {
    const codeSet = new Set(existingCodes.map(c => c.trim().toLowerCase()));
    const trimmed = originalCode.trim();

    const match = trimmed.match(/^(.*)\.([a-zA-Z])$/);
    let base = trimmed;
    let startLetterCode = 'b'.charCodeAt(0);
    let isUpper = false;

    if (match) {
      base = match[1];
      isUpper = match[2] === match[2].toUpperCase() && match[2] !== match[2].toLowerCase();
      const currentLetter = match[2].toLowerCase();
      startLetterCode = currentLetter.charCodeAt(0) + 1;
    }

    for (let i = 0; i < 26; i++) {
      const charCode = startLetterCode + i;
      if (charCode > 'z'.charCodeAt(0)) {
        const overflowIndex = charCode - 'z'.charCodeAt(0) - 1;
        let letter = String.fromCharCode('a'.charCodeAt(0) + overflowIndex);
        if (isUpper) letter = letter.toUpperCase();
        const candidate = `${base}.a${letter}`;
        if (!codeSet.has(candidate.toLowerCase())) {
          return candidate;
        }
      } else {
        let letter = String.fromCharCode(charCode);
        if (isUpper) letter = letter.toUpperCase();
        const candidate = `${base}.${letter}`;
        if (!codeSet.has(candidate.toLowerCase())) {
          return candidate;
        }
      }
    }

    return `${base}.copy-${Date.now().toString().slice(-4)}`;
  };

  const handleDuplicate = (obj: LearningObjective) => {
    const existingCodes = objectives.map(o => o.code);
    const newCode = getDuplicateTpCode(obj.code, existingCodes);
    const duplicatedObj: LearningObjective = {
      ...obj,
      id: `tp-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      code: newCode,
    };
    onUpdate([...objectives, duplicatedObj]);
  };

  const handleImportExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        const newObjectives: LearningObjective[] = data.map((row: any, index) => ({
          id: `tp-import-${Date.now()}-${index}`,
          code: String(row['Kode TP'] || row['Kode'] || `TP ${objectives.length + index + 1}`),
          element: row['Elemen'] ? String(row['Elemen']) : undefined,
          scope: row['Lingkup Materi'] ? String(row['Lingkup Materi']) : undefined,
          semester: row['Semester'] ? String(row['Semester']) : undefined,
          academicYear: row['Tahun Ajaran'] ? String(row['Tahun Ajaran']) : undefined,
          description: String(row['Deskripsi TP'] || row['Deskripsi'] || row['Tujuan Pembelajaran'] || ''),
          subject: userRole === UserRole.Admin 
            ? (row['Mata Pelajaran'] || row['Mapel'] || (selectedSubjectFilter !== 'Semua' ? selectedSubjectFilter : 'Umum'))
            : teacherSubject
        })).filter(obj => obj.description && obj.description !== 'undefined');

        if (newObjectives.length > 0) {
          onUpdate([...objectives, ...newObjectives]);
          alert(`Berhasil mengimpor ${newObjectives.length} Tujuan Pembelajaran.`);
        } else {
          alert('Tidak ada data yang valid ditemukan di file Excel. Pastikan kolom "Kode TP" dan "Deskripsi TP" tersedia.');
        }
      } catch (error) {
        console.error('Error importing Excel:', error);
        alert('Terjadi kesalahan saat membaca file Excel.');
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = ''; // Reset input
  };

  const handlePrint = () => {
    if (filteredObjectives.length === 0) {
      alert('Tidak ada data untuk dicetak.');
      return;
    }

    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Mohon izinkan pop-up untuk mencetak.');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cetak Tujuan Pembelajaran</title>
        <style>
          body { font-family: 'Times New Roman', Times, serif; padding: 20px; color: #000; line-height: 1.4; }
          .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
          .kop-logo { width: 80px; height: 80px; object-fit: contain; }
          .kop-text { flex: 1; text-align: center; }
          .kop-text p { margin: 0; line-height: 1.2; }
          .kop-gov { font-size: 16px; font-weight: bold; text-transform: uppercase; }
          .kop-dept { font-size: 16px; font-weight: bold; text-transform: uppercase; }
          .kop-school { font-size: 24px; font-weight: bold; text-transform: uppercase; }
          .kop-address { font-size: 12px; font-style: italic; }
          .kop-additional { font-size: 12px; }
          .kop-line-1 { height: 3px; background-color: #000; width: 100%; margin-top: 10px; }
          .kop-line-2 { height: 1px; background-color: #000; width: 100%; margin-top: 2px; }
          
          .report-title { text-align: center; font-size: 16px; font-weight: bold; margin: 20px 0; text-transform: uppercase; text-decoration: underline; }
          
          .meta-table { width: 100%; margin-bottom: 20px; font-size: 12px; }
          .meta-table td { padding: 2px 5px; vertical-align: top; }
          .meta-table td:first-child { width: 150px; }
          .meta-table td:nth-child(2) { width: 10px; }
          
          .data-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 30px; }
          .data-table th, .data-table td { border: 1px solid #000; padding: 8px; text-align: left; }
          table.data-table th { background-color: #f3f4f6; color: #000; font-weight: bold; text-align: center; }
          
          .signature-container { width: 100%; display: flex; justify-content: flex-end; margin-top: 40px; }
          .signature-box { width: 250px; text-align: center; font-size: 12px; }
          .signature-space { height: 80px; }
          .signature-name { font-weight: bold; text-decoration: underline; }
          
          @media print {
            @page { size: portrait; margin: 2cm; }
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="kop-container">
          <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
          <div class="kop-text">
            <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
            <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
            <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
            <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
            ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
          </div>
          <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
        </div>
        <div class="kop-line-1"></div>
        <div class="kop-line-2"></div>

        <div class="report-title">DAFTAR TUJUAN PEMBELAJARAN</div>

        <table class="meta-table">
          <tr>
            <td>Mata Pelajaran</td><td>:</td><td>${userRole === UserRole.Admin ? selectedSubjectFilter : teacherSubject}</td>
          </tr>
          <tr>
            <td>Nama Guru</td><td>:</td><td>${userRole === UserRole.Admin ? 'Super Admin' : activeUser.name}</td>
          </tr>
          <tr>
            <td>Tahun Pelajaran</td><td>:</td><td>${filteredObjectives.length > 0 ? (filteredObjectives[0].academicYear || '-') : '-'}</td>
          </tr>
          <tr>
            <td>Semester</td><td>:</td><td>${filteredObjectives.length > 0 ? (filteredObjectives[0].semester || '-') : '-'}</td>
          </tr>
        </table>

        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 40px;">No</th>
              <th style="width: 80px;">Kode TP</th>
              <th>Deskripsi Tujuan Pembelajaran</th>
              <th style="width: 100px;">Elemen</th>
              <th style="width: 100px;">Lingkup Materi</th>
              <th style="width: 60px;">Semester</th>
            </tr>
          </thead>
          <tbody>
            ${filteredObjectives.map((obj, idx) => `
              <tr>
                <td style="text-align: center;">${idx + 1}</td>
                <td style="text-align: center; font-weight: bold;">${obj.code}</td>
                <td>${obj.description}</td>
                <td>${obj.element || '-'}</td>
                <td>${obj.scope || '-'}</td>
                <td style="text-align: center;">${obj.semester || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="signature-container">
          <div class="signature-box">
            <p>${schoolSettings.city || 'Kawali'}, ${today}</p>
            <p>Guru Mata Pelajaran</p>
            <div class="signature-space"></div>
            <p class="signature-name">${activeUser.name}</p>
          </div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExportExcel = async () => {
    if (filteredObjectives.length === 0) {
      alert('Tidak ada data untuk diekspor.');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Tujuan_Pembelajaran');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    worksheet.columns = [
      { width: 5 },   // No
      { width: 15 },  // Kode TP
      { width: 50 },  // Deskripsi
      { width: 20 },  // Elemen
      { width: 20 },  // Lingkup Materi
      { width: 10 },  // Semester
      { width: 15 },  // Tahun Ajaran
      { width: 10 },  // Sumatif
      { width: 10 }   // Praktikum
    ];

    // Add Header (Kop) - simplified for Excel but can add images if needed like in AgendaView
    // For now let's just add the text header
    worksheet.mergeCells('A1:I1');
    worksheet.getCell('A1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('A1').font = { bold: true, size: 12 };
    worksheet.getCell('A1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A2:I2');
    worksheet.getCell('A2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('A2').font = { bold: true, size: 12 };
    worksheet.getCell('A2').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A3:I3');
    worksheet.getCell('A3').value = schoolSettings.schoolName || 'NAMA SEKOLAH';
    worksheet.getCell('A3').font = { bold: true, size: 14 };
    worksheet.getCell('A3').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A4:I4');
    worksheet.getCell('A4').value = schoolSettings.address || 'Alamat Sekolah...';
    worksheet.getCell('A4').font = { italic: true, size: 10 };
    worksheet.getCell('A4').alignment = { horizontal: 'center' };

    worksheet.addRow([]); // Empty row
    
    worksheet.mergeCells('A6:I6');
    worksheet.getCell('A6').value = 'DAFTAR TUJUAN PEMBELAJARAN';
    worksheet.getCell('A6').font = { bold: true, size: 12 };
    worksheet.getCell('A6').alignment = { horizontal: 'center' };

    worksheet.addRow([]); // Empty row

    worksheet.getCell('A8').value = 'Mata Pelajaran:';
    worksheet.getCell('B8').value = userRole === UserRole.Admin ? selectedSubjectFilter : teacherSubject;
    worksheet.getCell('A9').value = 'Nama Guru:';
    worksheet.getCell('B9').value = userRole === UserRole.Admin ? 'Super Admin' : activeUser.name;

    worksheet.addRow([]); // Empty row

    // Table Header
    const headerRow = worksheet.addRow(['No', 'Kode TP', 'Deskripsi Tujuan Pembelajaran', 'Elemen', 'Lingkup Materi', 'Semester', 'Tahun Ajaran', 'Sumatif', 'Praktikum']);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Data
    filteredObjectives.forEach((obj, idx) => {
      const row = worksheet.addRow([
        idx + 1,
        obj.code,
        obj.description,
        obj.element || '-',
        obj.scope || '-',
        obj.semester || '-',
        obj.academicYear || '-',
        obj.isSummative ? 'Ya' : 'Tidak',
        obj.isPracticum ? 'Ya' : 'Tidak'
      ]);
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'middle', wrapText: true };
      });
    });

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    const filenameSubject = userRole === UserRole.Admin ? selectedSubjectFilter : teacherSubject;
    saveAs(new Blob([buffer]), `Tujuan_Pembelajaran_${filenameSubject.replace(/\s+/g, '_')}.xlsx`);
  };

  const totalTp = filteredObjectives.length;
  const summativeCount = useMemo(() => filteredObjectives.filter(o => o.isSummative).length, [filteredObjectives]);
  const practicumCount = useMemo(() => filteredObjectives.filter(o => o.isPracticum).length, [filteredObjectives]);

  return (
    <div className="space-y-6 animate-fade-in pb-20">
      {/* Sync Status Toast Notification */}
      {syncToast && (
        <div className="fixed top-6 right-6 z-50 bg-slate-900/90 backdrop-blur-md text-white px-5 py-3 rounded-2xl shadow-xl border border-white/10 flex items-center gap-3 animate-fade-in-up text-xs font-bold">
          <Check size={16} className="text-emerald-400" />
          <span>{syncToast}</span>
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-[2rem] md:rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800">
        
        {/* Banner & Header Section */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center pb-6 border-b border-slate-100 dark:border-slate-800 gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-0.5 shadow-lg shadow-indigo-500/20 shrink-0">
              <div className="w-full h-full bg-white dark:bg-slate-900 rounded-[14px] flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                <Target size={28} />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl md:text-3xl font-black text-slate-800 dark:text-white tracking-tight">
                  Tujuan Pembelajaran (TP)
                </h2>
                <button
                  onClick={handleSyncFromDatabase}
                  disabled={isSyncing}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold transition-all disabled:opacity-50"
                  title="Sinkronkan dan muat ulang semua TP langsung dari Firestore Database"
                >
                  <RefreshCw size={12} className={isSyncing ? "animate-spin text-indigo-600" : ""} />
                  <span>{isSyncing ? "Memuat Database..." : "Sinkronkan DB"}</span>
                </button>
              </div>

              {/* Filter controls row */}
              {userRole === UserRole.Admin ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500 font-bold">Mapel:</span>
                    <select
                      value={selectedSubjectFilter}
                      onChange={(e) => setSelectedSubjectFilter(e.target.value)}
                      className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold rounded-xl border border-indigo-200/60 dark:border-indigo-800/40 outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs cursor-pointer shadow-xs"
                    >
                      <option value="Semua">Semua Mapel ({availableSubjectsFromObjectives.length})</option>
                      {availableSubjectsFromObjectives.map(subj => (
                        <option key={subj} value={subj}>{subj}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500 font-bold">Tahun:</span>
                    <select
                      value={selectedYearFilter}
                      onChange={(e) => setSelectedYearFilter(e.target.value)}
                      className="px-2.5 py-1 bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 font-bold rounded-xl border border-amber-200/60 dark:border-amber-800/40 outline-none focus:ring-2 focus:ring-amber-500/20 text-xs cursor-pointer shadow-xs"
                    >
                      <option value="Semua">Semua Tahun</option>
                      {availableAcademicYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500 font-bold">Semester:</span>
                    <select
                      value={selectedSemesterFilter}
                      onChange={(e) => setSelectedSemesterFilter(e.target.value)}
                      className="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 font-bold rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs cursor-pointer shadow-xs"
                    >
                      <option value="Semua">Semua Semester</option>
                      {availableSemesters.map(sem => (
                        <option key={sem} value={sem}>{sem}</option>
                      ))}
                    </select>
                  </div>

                  {isAnyFilterActive && (
                    <button
                      onClick={resetFilters}
                      className="px-2.5 py-1 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 hover:bg-rose-100 rounded-xl text-xs font-extrabold border border-rose-200 dark:border-rose-900/40 transition-colors flex items-center gap-1"
                      title="Reset semua filter untuk melihat seluruh TP di database"
                    >
                      <X size={12} />
                      <span>Reset Filter</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-2">
                  <p className="text-xs text-slate-500 font-medium">
                    Mata Pelajaran: <span className="text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-950/60 px-2.5 py-0.5 rounded-lg border border-indigo-200/50 dark:border-indigo-800/40">{teacherSubject}</span>
                  </p>

                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500 font-bold">Tahun:</span>
                    <select
                      value={selectedYearFilter}
                      onChange={(e) => setSelectedYearFilter(e.target.value)}
                      className="px-2.5 py-1 bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 font-bold rounded-xl border border-amber-200/60 dark:border-amber-800/40 outline-none focus:ring-2 focus:ring-amber-500/20 text-xs cursor-pointer shadow-xs"
                    >
                      <option value="Semua">Semua Tahun</option>
                      {availableAcademicYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500 font-bold">Semester:</span>
                    <select
                      value={selectedSemesterFilter}
                      onChange={(e) => setSelectedSemesterFilter(e.target.value)}
                      className="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 font-bold rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs cursor-pointer shadow-xs"
                    >
                      <option value="Semua">Semua Semester</option>
                      {availableSemesters.map(sem => (
                        <option key={sem} value={sem}>{sem}</option>
                      ))}
                    </select>
                  </div>

                  {isAnyFilterActive && (
                    <button
                      onClick={resetFilters}
                      className="px-2.5 py-1 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 hover:bg-rose-100 rounded-xl text-xs font-extrabold border border-rose-200 dark:border-rose-900/40 transition-colors flex items-center gap-1"
                    >
                      <X size={12} />
                      <span>Reset Filter</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="flex items-center gap-2 sm:gap-3 w-full lg:w-auto overflow-x-auto pb-1 lg:pb-0">
            <div className="flex-1 lg:flex-none flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/60 shadow-sm min-w-[120px]">
              <div className="w-8 h-8 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                <Target size={18} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Total Ditampilkan</p>
                <p className="text-base font-black text-slate-800 dark:text-white">
                  {totalTp} <span className="text-xs text-slate-400 font-normal">/ {objectives.length} DB</span>
                </p>
              </div>
            </div>

            <div className="flex-1 lg:flex-none flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-rose-50/50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/40 shadow-sm min-w-[110px]">
              <div className="w-8 h-8 rounded-xl bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                <Award size={18} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-rose-500 dark:text-rose-400">Sumatif</p>
                <p className="text-base font-black text-rose-700 dark:text-rose-300">{summativeCount}</p>
              </div>
            </div>

            <div className="flex-1 lg:flex-none flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-emerald-50/50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40 shadow-sm min-w-[110px]">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <Sparkles size={18} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Praktikum</p>
                <p className="text-base font-black text-emerald-700 dark:text-emerald-300">{practicumCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Controls & Search Toolbar */}
        <div className="flex flex-col xl:flex-row justify-between items-center my-6 gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full xl:w-auto">
            {/* Search Box */}
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder="Cari ID, kode, mapel, deskripsi..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:text-white font-semibold text-sm transition-all shadow-inner"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')} 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Sort Controls */}
            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl px-3 py-1.5 w-full sm:w-auto justify-between">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Urut:</span>
              <select 
                value={sortBy} 
                onChange={e => setSortBy(e.target.value as any)}
                className="bg-transparent border-none outline-none text-xs font-bold text-slate-700 dark:text-slate-300 py-1 cursor-pointer"
              >
                <option value="code">Kode TP</option>
                <option value="description">Deskripsi</option>
                <option value="semester">Semester</option>
              </select>
              <button 
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="p-1.5 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors rounded-lg hover:bg-slate-200/60 dark:hover:bg-slate-700/60"
                title={sortOrder === 'asc' ? 'Urutan Naik' : 'Urutan Turun'}
              >
                <ArrowUpDown size={15} className={sortOrder === 'desc' ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>
            </div>
          </div>

          {/* Action Buttons Group */}
          <div className="flex items-center gap-2.5 w-full xl:w-auto overflow-x-auto">
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleImportExcel} 
              accept=".xlsx, .xls" 
              className="hidden" 
            />
            <button 
              onClick={() => fileInputRef.current?.click()} 
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-bold rounded-2xl hover:bg-emerald-700 shadow-md shadow-emerald-500/20 active:scale-95 transition-all text-xs md:text-sm"
              title="Impor dari Excel"
            >
              <Upload size={16} /> <span>Impor</span>
            </button>
            <button 
              onClick={handleExportExcel} 
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200/80 dark:border-slate-700/80 active:scale-95 transition-all text-xs md:text-sm"
              title="Ekspor ke Excel"
            >
              <FileSpreadsheet size={16} /> <span>Ekspor</span>
            </button>
            <button 
              onClick={handlePrint} 
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200/80 dark:border-slate-700/80 active:scale-95 transition-all text-xs md:text-sm"
              title="Cetak TP"
            >
              <Printer size={16} /> <span>Cetak</span>
            </button>
            <button 
              onClick={() => handleOpenModal()} 
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold rounded-2xl hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/25 active:scale-95 transition-all text-xs md:text-sm shrink-0"
            >
              <Plus size={18} /> <span>Tambah TP</span>
            </button>
          </div>
        </div>

        {/* Objectives Representation: Table for Admin, Cards for Teacher */}
        {userRole === UserRole.Admin ? (
          <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-950 shadow-sm mt-4">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-[10px] font-black uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-4 w-12 text-center">No</th>
                  <th className="px-5 py-4">ID TP (Database)</th>
                  <th className="px-5 py-4">Kode</th>
                  <th className="px-5 py-4">Mata Pelajaran</th>
                  <th className="px-5 py-4">Elemen / Lingkup</th>
                  <th className="px-5 py-4">Periode</th>
                  <th className="px-5 py-4">Deskripsi TP</th>
                  <th className="px-5 py-4 text-center">Status</th>
                  <th className="px-5 py-4 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                {filteredObjectives.map((obj, index) => (
                  <tr key={obj.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 text-xs font-semibold text-slate-700 dark:text-slate-300 transition-colors">
                    <td className="px-5 py-3 text-center text-slate-400 font-mono text-[11px]">
                      {index + 1}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-indigo-600 dark:text-indigo-400">
                      <div className="flex items-center gap-1.5">
                        <span className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200/50 dark:border-slate-700/50 text-[11px] font-medium truncate max-w-[130px]" title={obj.id}>
                          {obj.id}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(obj.id);
                            alert(`ID TP [${obj.id}] berhasil disalin ke clipboard!`);
                          }}
                          className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded text-slate-400 hover:text-indigo-600 transition-colors shrink-0"
                          title="Salin ID Database"
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-black text-xs text-slate-900 dark:text-white whitespace-nowrap">
                      <span className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-200/60 dark:border-slate-700/60">
                        {obj.code || '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-lg text-xs font-bold border border-indigo-100 dark:border-indigo-900/50">
                        {obj.subject || '-'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-400 max-w-[160px] truncate" title={`${obj.element || ''} ${obj.scope ? `(${obj.scope})` : ''}`}>
                      <div className="truncate font-medium">{obj.element || '-'}</div>
                      {obj.scope && <div className="text-[10px] text-slate-400 truncate">{obj.scope}</div>}
                    </td>
                    <td className="px-5 py-3 text-xs whitespace-nowrap">
                      <div className="font-bold text-slate-700 dark:text-slate-300">Sem. {obj.semester || '-'}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{obj.academicYear || '-'}</div>
                    </td>
                    <td className="px-5 py-3">
                      {/* Interactive Compact Click-to-View Description Button */}
                      <button
                        type="button"
                        onClick={() => setSelectedDescriptionObj(obj)}
                        className="group flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-100/90 dark:bg-slate-800/90 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 border border-slate-200/80 dark:border-slate-700/80 hover:border-indigo-300 dark:hover:border-indigo-800 transition-all text-xs max-w-[280px] w-full text-left shadow-xs cursor-pointer"
                        title="Klik untuk membuka deskripsi lengkap TP"
                      >
                        <BookOpen size={13} className="text-indigo-500 shrink-0 group-hover:scale-110 transition-transform" />
                        <span className="truncate flex-1 font-normal text-xs text-slate-600 dark:text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-300">
                          {obj.description ? obj.description.replace(/\n+/g, ' ') : '(Klik untuk isi deskripsi)'}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-100/80 dark:bg-indigo-900/60 px-1.5 py-0.5 rounded-md shrink-0 border border-indigo-200/50 dark:border-indigo-800/50">
                          Lihat
                        </span>
                      </button>
                    </td>
                    <td className="px-5 py-3 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {obj.isSummative && (
                          <span className="px-2 py-0.5 bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/25 rounded-md text-[10px] font-extrabold uppercase">
                            Sumatif
                          </span>
                        )}
                        {obj.isPracticum && (
                          <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25 rounded-md text-[10px] font-extrabold uppercase">
                            Praktikum
                          </span>
                        )}
                        {!obj.isSummative && !obj.isPracticum && <span className="text-slate-400 font-medium text-[11px]">Formatif</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <button 
                          onClick={() => handleDuplicate(obj)} 
                          title="Duplikat TP" 
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all"
                        >
                          <Copy size={13} />
                        </button>
                        <button
                          onClick={() => handleOpenModal(obj)}
                          className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all"
                          title="Edit TP"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          onClick={() => handleDelete(obj.id)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all"
                          title="Hapus TP"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredObjectives.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-slate-400">
                      <Target className="mx-auto mb-3 text-slate-300 dark:text-slate-700" size={40} />
                      <p className="font-bold text-sm">Tidak ada Tujuan Pembelajaran yang sesuai dengan filter.</p>
                      {isAnyFilterActive && (
                        <button
                          onClick={resetFilters}
                          className="mt-3 px-4 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl text-xs font-bold hover:bg-indigo-100 transition-colors"
                        >
                          Tampilkan Semua {objectives.length} TP di Database
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pt-2">
            {filteredObjectives.map((obj, index) => {
              const colors = getColorClasses(index);
              return (
                <div 
                  key={obj.id} 
                  className={`group relative bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-xl ${colors.cardBorder} transition-all duration-300 flex flex-col justify-between overflow-hidden hover:-translate-y-1`}
                >
                  {/* Solid Gradient Top Header Bar */}
                  <div className={`bg-gradient-to-r ${colors.gradient} text-white px-4 py-3 flex items-center justify-between gap-2 shadow-sm`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2.5 py-0.5 bg-white/20 backdrop-blur-md rounded-lg text-xs font-black uppercase tracking-wider text-white border border-white/25 flex items-center gap-1 shadow-sm">
                        <Target size={13} />
                        {obj.code}
                      </span>
                      {obj.isSummative && (
                        <span className="px-2 py-0.5 bg-rose-500/80 backdrop-blur-md rounded-lg text-[10px] font-extrabold uppercase tracking-wider text-white border border-rose-400/30 flex items-center gap-1 shadow-sm">
                          <Award size={11} /> Sumatif
                        </span>
                      )}
                      {obj.isPracticum && (
                        <span className="px-2 py-0.5 bg-emerald-500/80 backdrop-blur-md rounded-lg text-[10px] font-extrabold uppercase tracking-wider text-white border border-emerald-400/30 flex items-center gap-1 shadow-sm">
                          <Sparkles size={11} /> Praktikum
                        </span>
                      )}
                    </div>

                    {/* Integrated Action Buttons */}
                    <div className="flex items-center gap-1 bg-black/20 backdrop-blur-md p-1 rounded-xl border border-white/20">
                      <button 
                        onClick={() => handleDuplicate(obj)} 
                        title="Duplikat" 
                        className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all"
                      >
                        <Copy size={13} />
                      </button>
                      <button 
                        onClick={() => handleOpenModal(obj)} 
                        title="Edit" 
                        className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all"
                      >
                        <Edit2 size={13} />
                      </button>
                      <button 
                        onClick={() => handleDelete(obj.id)} 
                        title="Hapus" 
                        className="p-1 text-white/80 hover:text-rose-200 hover:bg-rose-500/40 rounded-lg transition-all"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Card Content Body */}
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div className="space-y-3">
                      {/* Description with click-to-expand modal */}
                      <div 
                        onClick={() => setSelectedDescriptionObj(obj)}
                        className="cursor-pointer group/desc p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border border-transparent hover:border-slate-200/60 dark:hover:border-slate-700/60"
                        title="Klik untuk membuka deskripsi lengkap"
                      >
                        <p className="text-slate-800 dark:text-slate-100 font-semibold text-xs sm:text-sm leading-relaxed line-clamp-3">
                          {obj.description}
                        </p>
                        <div className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 dark:text-indigo-400 mt-1.5 opacity-0 group-hover/desc:opacity-100 transition-opacity">
                          <Eye size={12} />
                          <span>Buka Deskripsi Lengkap</span>
                        </div>
                      </div>

                      {/* Elemen & Scope in compact pill tags */}
                      {(obj.element || obj.scope) && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {obj.element && (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg ${colors.lightBg} text-[11px] font-medium text-slate-700 dark:text-slate-300 max-w-full truncate`}>
                              <Layers size={11} className={colors.accentText} />
                              <span className="font-bold">Elemen:</span> {obj.element}
                            </span>
                          )}
                          {obj.scope && (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg ${colors.lightBg} text-[11px] font-medium text-slate-700 dark:text-slate-300 max-w-full truncate`}>
                              <BookOpen size={11} className={colors.accentText} />
                              <span className="font-bold">Lingkup:</span> {obj.scope}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Footer Info */}
                    <div className="pt-3 mt-3 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[10px] font-bold text-slate-400">
                      <div className="flex items-center gap-1.5">
                        {obj.semester && (
                          <span className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md text-slate-600 dark:text-slate-300">
                            <Calendar size={11} className="text-amber-500" />
                            <span>Semester {obj.semester}</span>
                          </span>
                        )}
                      </div>
                      {obj.academicYear && (
                        <span className="bg-slate-50 dark:bg-slate-800/60 px-2 py-0.5 rounded border border-slate-200/60 dark:border-slate-700/60">
                          TH {obj.academicYear}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredObjectives.length === 0 && (
              <div className="col-span-full py-16 md:py-20 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[2rem] md:rounded-[2.5rem]">
                <Target className="mx-auto mb-4 text-slate-300" size={48} />
                <p className="text-slate-400 font-bold italic text-sm md:text-base">Belum ada Tujuan Pembelajaran untuk mata pelajaran ini.</p>
                {isAnyFilterActive && (
                  <button
                    onClick={resetFilters}
                    className="mt-3 px-4 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl text-xs font-bold hover:bg-indigo-100 transition-colors"
                  >
                    Reset Filter
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Description Popup Modal */}
      {selectedDescriptionObj && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl w-full max-w-xl p-6 md:p-8 animate-fade-in-up border border-slate-200 dark:border-slate-800 space-y-5">
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center border border-indigo-500/20 shrink-0">
                  <BookOpen size={24} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-0.5 bg-indigo-600 text-white rounded-lg text-xs font-black">
                      {selectedDescriptionObj.code || 'TP'}
                    </span>
                    <span className="px-2.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-bold border border-slate-200 dark:border-slate-700">
                      {selectedDescriptionObj.subject || 'Umum'}
                    </span>
                  </div>
                  <h4 className="text-lg font-black text-slate-900 dark:text-white mt-1">
                    Detail Tujuan Pembelajaran
                  </h4>
                </div>
              </div>
              <button
                onClick={() => setSelectedDescriptionObj(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors shrink-0"
              >
                <X size={20} />
              </button>
            </div>

            {/* Meta Information Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs bg-slate-50 dark:bg-slate-800/50 p-3.5 rounded-2xl border border-slate-200/60 dark:border-slate-700/60">
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-0.5">Database ID</span>
                <div className="flex items-center gap-1 font-mono text-[11px] text-indigo-600 dark:text-indigo-400">
                  <span className="truncate max-w-[100px]">{selectedDescriptionObj.id}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedDescriptionObj.id);
                      alert(`ID TP [${selectedDescriptionObj.id}] berhasil disalin!`);
                    }}
                    className="p-0.5 hover:text-indigo-800 dark:hover:text-indigo-200"
                    title="Salin ID"
                  >
                    <Copy size={11} />
                  </button>
                </div>
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-0.5">Periode</span>
                <span className="font-bold text-slate-700 dark:text-slate-300">
                  Sem. {selectedDescriptionObj.semester || '-'} • {selectedDescriptionObj.academicYear || '-'}
                </span>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-0.5">Status Penilaian</span>
                <div className="flex items-center gap-1">
                  {selectedDescriptionObj.isSummative && (
                    <span className="px-2 py-0.5 bg-rose-500/15 text-rose-600 dark:text-rose-400 font-extrabold text-[10px] uppercase rounded-md border border-rose-500/20">
                      Sumatif
                    </span>
                  )}
                  {selectedDescriptionObj.isPracticum && (
                    <span className="px-2 py-0.5 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-extrabold text-[10px] uppercase rounded-md border border-emerald-500/20">
                      Praktikum
                    </span>
                  )}
                  {!selectedDescriptionObj.isSummative && !selectedDescriptionObj.isPracticum && (
                    <span className="text-slate-400 font-medium">Formatif Standar</span>
                  )}
                </div>
              </div>
            </div>

            {/* Full Description Box */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Teks Deskripsi Lengkap
                </label>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(selectedDescriptionObj.description || '');
                    setCopiedDesc(true);
                    setTimeout(() => setCopiedDesc(false), 2000);
                  }}
                  className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                >
                  <Copy size={12} />
                  <span>{copiedDesc ? 'Tersalin!' : 'Salin Deskripsi'}</span>
                </button>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 text-sm leading-relaxed whitespace-pre-wrap max-h-56 overflow-y-auto font-medium shadow-inner select-text">
                {selectedDescriptionObj.description || '(Tidak ada deskripsi teks)'}
              </div>
            </div>

            {/* Additional Details (Element & Scope) */}
            {(selectedDescriptionObj.element || selectedDescriptionObj.scope) && (
              <div className="space-y-2">
                {selectedDescriptionObj.element && (
                  <div className="text-xs flex items-start gap-2 bg-indigo-50/60 dark:bg-indigo-950/40 p-3 rounded-xl border border-indigo-100 dark:border-indigo-900/50">
                    <Layers size={14} className="text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold text-indigo-900 dark:text-indigo-300">Elemen:</span>{' '}
                      <span className="text-slate-700 dark:text-slate-300">{selectedDescriptionObj.element}</span>
                    </div>
                  </div>
                )}
                {selectedDescriptionObj.scope && (
                  <div className="text-xs flex items-start gap-2 bg-purple-50/60 dark:bg-purple-950/40 p-3 rounded-xl border border-purple-100 dark:border-purple-900/50">
                    <BookOpen size={14} className="text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold text-purple-900 dark:text-purple-300">Lingkup Materi:</span>{' '}
                      <span className="text-slate-700 dark:text-slate-300">{selectedDescriptionObj.scope}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modal Footer Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => {
                  const objToEdit = selectedDescriptionObj;
                  setSelectedDescriptionObj(null);
                  handleOpenModal(objToEdit);
                }}
                className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Edit2 size={13} />
                <span>Edit TP Ini</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedDescriptionObj(null)}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-indigo-500/20 cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] md:rounded-[2.5rem] shadow-2xl w-full max-w-lg p-6 md:p-8 animate-fade-in-up border border-slate-200 dark:border-slate-800">
            <div className="flex justify-between items-center mb-6 md:mb-8">
              <h3 className="text-xl md:text-2xl font-black text-slate-800 dark:text-white">{editingId ? 'Edit TP' : 'Tambah TP Baru'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"><X size={24} /></button>
            </div>
            <div className="space-y-5 md:space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar pr-2">
              {userRole === UserRole.Admin && (
                <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">Mata Pelajaran</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <span className="text-[9px] text-slate-400 block mb-1 font-bold">Pilih Mapel Terdaftar:</span>
                      <select 
                        value={formData.subject} 
                        onChange={e => {
                          const val = e.target.value;
                          setFormData({...formData, subject: val === 'custom' ? '' : val});
                        }} 
                        className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-bold dark:text-white transition-all text-xs"
                      >
                        <option value="">Pilih Mapel</option>
                        {availableSubjectsFromObjectives.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                        <option value="custom">-- Mapel Baru (Ketik Manual) --</option>
                      </select>
                    </div>
                    <div>
                      <span className="text-[9px] text-slate-400 block mb-1 font-bold">Ketik Manual (Jika Mapel Baru):</span>
                      <input 
                        type="text" 
                        value={formData.subject} 
                        onChange={e => setFormData({...formData, subject: e.target.value})} 
                        className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-bold dark:text-white transition-all text-xs"
                        placeholder="Contoh: Informatika"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Kode TP</label>
                  <input type="text" value={formData.code} onChange={e => setFormData({...formData, code: e.target.value})} className="w-full px-4 md:px-5 py-3 md:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl md:rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-bold dark:text-white transition-all"/>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Tahun Ajaran</label>
                  <input type="text" value={formData.academicYear} onChange={e => setFormData({...formData, academicYear: e.target.value})} className="w-full px-4 md:px-5 py-3 md:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl md:rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-bold dark:text-white transition-all" placeholder="Contoh: 2023/2024"/>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Semester</label>
                  <select value={formData.semester} onChange={e => setFormData({...formData, semester: e.target.value})} className="w-full px-4 md:px-5 py-3 md:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl md:rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-bold dark:text-white transition-all">
                    <option value="">Pilih Semester</option>
                    <option value="Ganjil">Ganjil</option>
                    <option value="Genap">Genap</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Elemen</label>
                  <input type="text" value={formData.element} onChange={e => setFormData({...formData, element: e.target.value})} className="w-full px-4 md:px-5 py-3 md:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl md:rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-bold dark:text-white transition-all" placeholder="Contoh: Menyimak"/>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Lingkup Materi</label>
                <input type="text" value={formData.scope} onChange={e => setFormData({...formData, scope: e.target.value})} className="w-full px-4 md:px-5 py-3 md:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl md:rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-bold dark:text-white transition-all" placeholder="Contoh: Teks Laporan Hasil Observasi"/>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Isi Tujuan Pembelajaran</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={4} className="w-full px-4 md:px-5 py-3 md:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl md:rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-bold dark:text-white transition-all resize-none" placeholder="Contoh: Peserta didik mampu mengidentifikasi..."></textarea>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <input 
                    type="checkbox" 
                    checked={formData.isSummative} 
                    onChange={e => setFormData({...formData, isSummative: e.target.checked})} 
                    className="w-5 h-5 rounded-lg border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Sumatif</label>
                </div>
                <div className="flex items-center gap-3 flex-1">
                  <input 
                    type="checkbox" 
                    checked={formData.isPracticum} 
                    onChange={e => setFormData({...formData, isPracticum: e.target.checked})} 
                    className="w-5 h-5 rounded-lg border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Praktikum</label>
                </div>
              </div>
              <button onClick={handleSave} className="w-full py-4 md:py-5 bg-indigo-600 text-white font-black rounded-xl md:rounded-2xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 active:scale-95 transition-all uppercase tracking-widest text-xs md:text-sm mt-2">SIMPAN TUJUAN PEMBELAJARAN</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default LearningObjectivesView;
